import React, { useState, useEffect, memo } from "react";
import {
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Autocomplete,
  Chip,
  Checkbox,
  IconButton,
  Stack,
  CircularProgress,
  Typography,
  createFilterOptions,
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import SaveIcon from "@mui/icons-material/Save";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import configServ from "../../services/config";
import toast from "react-hot-toast";

const initialState = { supervisor_id: "", subordinate_ids: [] };

// Helper to format employee full name
const getEmployeeName = (emp) => {
  if (!emp) return "";
  return (
    emp.contact_name ||
    `${emp.fname || ""} ${emp.lname || ""}`.trim() ||
    emp.mobile ||
    ""
  );
};

// Helper to format display label: Name (Designation/Department)
const formatEmployeeLabel = (emp) => {
  if (!emp) return "";
  const name = getEmployeeName(emp);
  const details = [emp.designation, emp.department].filter(Boolean).join("/");
  return details ? `${name} (${details})` : name;
};

// Search filter that matches Name, Designation, Department, Employee Code, Mobile, and Email
const filterEmployeeOptions = createFilterOptions({
  stringify: (option) =>
    [
      option.contact_name,
      option.fname,
      option.lname,
      option.designation,
      option.department,
      option.employee_code,
      option.mobile,
      option.email,
      formatEmployeeLabel(option),
    ]
      .filter(Boolean)
      .join(" "),
});

function EmployeeHierarchyCard({ company_id, setIsCreate, setIsChanged }) {
  const [form, setForm] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [siteList, setSiteList] = useState([]);
  const [fetchingDropdowns, setFetchingDropdowns] = useState(false);
  const [siteFilter, setSiteFilter] = useState("");

  // Fetch Sites (Filtered by type "Standard")
  useEffect(() => {
    const fetchSiteList = async () => {
      try {
        const result = await configServ.get_customer({ company_id });
        const filteredSites = result?.filter((item) => item.type === "Standard");
        setSiteList(filteredSites || []);
      } catch (e) {
        console.error("Error fetching site list:", e);
      }
    };

    if (company_id) {
      fetchSiteList();
    }
  }, [company_id]);

  // Fetch Employees based on company_id and siteFilter
  useEffect(() => {
    const loadEmployees = async () => {
      setFetchingDropdowns(true);
      try {
        const payload = { company_id };
        if (siteFilter) {
          payload.site_id = siteFilter;
        }
        const res = await configServ.getSupervisors(payload);
        if (res?.status) {
          setEmployees(res.data || []);
        } else {
          setEmployees([]);
        }
      } catch (e) {
        console.error("Error fetching employees:", e);
        setEmployees([]);
      } finally {
        setFetchingDropdowns(false);
      }
    };

    if (company_id) {
      loadEmployees();
    }
  }, [company_id, siteFilter]);

  const handleSiteChange = (e) => {
    const selectedSite = e.target.value;
    setSiteFilter(selectedSite);
    // Reset selections when switching site
    setForm(initialState);
    setErrors({});
  };

  const handleSupervisorChange = (_, newValue) => {
    const newSupervisorId = newValue ? newValue.id : "";
    setForm((prev) => ({
      ...prev,
      supervisor_id: newSupervisorId,
      // Remove newly selected supervisor from subordinates if previously selected
      subordinate_ids: prev.subordinate_ids.filter((id) => id !== newSupervisorId),
    }));
    if (errors.supervisor_id) {
      setErrors((prev) => ({ ...prev, supervisor_id: "" }));
    }
  };

  const handleSubordinatesChange = (_, newValue) => {
    const ids = newValue ? newValue.map((item) => item.id) : [];
    setForm((prev) => ({ ...prev, subordinate_ids: ids }));
    if (errors.subordinate_ids) {
      setErrors((prev) => ({ ...prev, subordinate_ids: "" }));
    }
  };

  const handleReset = () => {
    setForm(initialState);
    setErrors({});
  };

  const validate = () => {
    const err = {};
    if (!form.supervisor_id) err.supervisor_id = "Supervisor is required";
    if (!form.subordinate_ids.length) {
      err.subordinate_ids = "Select at least one subordinate";
    }
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const res = await configServ.assignEmployeeHierarchy({
        company_id,
        supervisor_id: form.supervisor_id,
        subordinate_ids: form.subordinate_ids,
      });

      if (res?.status) {
        const inserted = res.inserted ?? 0;
        const skipped = res.skipped ?? 0;
        toast.success(`Assigned: ${inserted} new, ${skipped} already existed`);
        handleReset();
        setIsCreate(false);
        setIsChanged((p) => !p);
      } else {
        toast.error(res?.message || "Operation failed");
      }
    } catch (e) {
      toast.error("Something went wrong");
      console.error("Assign error:", e);
    } finally {
      setLoading(false);
    }
  };

  // Selected object for Supervisor Autocomplete
  const selectedSupervisor =
    employees.find((e) => e.id === form.supervisor_id) || null;

  // Subordinate options (excluding currently selected supervisor)
  const subordinateOptions = employees.filter(
    (e) => e.id !== form.supervisor_id
  );

  // Selected array of objects for Subordinates Autocomplete
  const selectedSubordinates = employees.filter((e) =>
    form.subordinate_ids.includes(e.id)
  );

  return (
    <Box
      sx={{
        backgroundColor: "#e3f3fc",
        p: 2.5,
        borderRadius: 2,
        m: 1,
        boxShadow: 1,
      }}
    >
      <Grid container spacing={2} alignItems="flex-start">
        {/* 1. Site Dropdown */}
        <Grid item xs={12} sm={6} md={3} lg={2.5}>
          <FormControl fullWidth size="small">
            <InputLabel id="site-select-label">Site</InputLabel>
            <Select
              labelId="site-select-label"
              id="site-select"
              value={siteFilter}
              label="Site"
              onChange={handleSiteChange}
              sx={{ backgroundColor: "white" }}
            >
              <MenuItem value="">
                <em>All</em>
              </MenuItem>
              {siteList.map((site) => (
                <MenuItem
                  key={site.id || site.site_id}
                  value={site.id || site.site_id}
                >
                  {site.contact_name ||
                    site.site_name ||
                    site.name ||
                    `Site ${site.id || site.site_id}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* 2. Supervisor Autocomplete (Filterable by Name, Designation, Department) */}
        <Grid item xs={12} sm={6} md={4} lg={4}>
          <Autocomplete
            size="small"
            id="supervisor-autocomplete"
            options={employees}
            loading={fetchingDropdowns}
            value={selectedSupervisor}
            onChange={handleSupervisorChange}
            getOptionLabel={(option) => formatEmployeeLabel(option)}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            filterOptions={filterEmployeeOptions}
            renderOption={(props, option) => {
              const name = getEmployeeName(option);
              const details = [option.designation, option.department]
                .filter(Boolean)
                .join("/");
              return (
                <Box
                  component="li"
                  {...props}
                  key={option.id}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start !important",
                    py: 0.75,
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, color: "text.primary" }}
                  >
                    {name}
                  </Typography>
                  {details && (
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary", mt: 0.2 }}
                    >
                      {details}
                      {option.employee_code ? ` • Code: ${option.employee_code}` : ""}
                    </Typography>
                  )}
                </Box>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Supervisor *"
                placeholder={fetchingDropdowns ? "Loading..." : "Search name, designation, dept..."}
                error={!!errors.supervisor_id}
                helperText={errors.supervisor_id}
                sx={{ backgroundColor: "white" }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {fetchingDropdowns ? (
                        <CircularProgress color="inherit" size={18} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            noOptionsText="No employees found"
          />
        </Grid>

        {/* 3. Subordinates Multi-select Autocomplete (Filterable by Name, Designation, Department) */}
        <Grid item xs={12} sm={12} md={5} lg={5.5}>
          <Autocomplete
            multiple
            size="small"
            id="subordinates-autocomplete"
            options={subordinateOptions}
            loading={fetchingDropdowns}
            value={selectedSubordinates}
            onChange={handleSubordinatesChange}
            disableCloseOnSelect
            getOptionLabel={(option) => formatEmployeeLabel(option)}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            filterOptions={filterEmployeeOptions}
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                return (
                  <Chip
                    key={option.id || key}
                    {...tagProps}
                    label={formatEmployeeLabel(option)}
                    size="small"
                    sx={{ my: 0.25, maxWidth: "100%" }}
                  />
                );
              })
            }
            renderOption={(props, option, { selected }) => {
              const name = getEmployeeName(option);
              const details = [option.designation, option.department]
                .filter(Boolean)
                .join("/");
              return (
                <Box
                  component="li"
                  {...props}
                  key={option.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    py: 0.5,
                    borderBottom: "1px solid #f0f0f0",
                  }}
                >
                  <Checkbox
                    icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                    checkedIcon={<CheckBoxIcon fontSize="small" />}
                    style={{ marginRight: 8 }}
                    checked={selected}
                    size="small"
                  />
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 500, color: "text.primary" }}
                    >
                      {name}
                    </Typography>
                    {details && (
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary" }}
                      >
                        {details}
                        {option.employee_code ? ` • Code: ${option.employee_code}` : ""}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Subordinates *"
                placeholder={
                  form.subordinate_ids.length === 0
                    ? fetchingDropdowns
                      ? "Loading..."
                      : "Search & select subordinates..."
                    : ""
                }
                error={!!errors.subordinate_ids}
                helperText={errors.subordinate_ids}
                sx={{ backgroundColor: "white" }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {fetchingDropdowns ? (
                        <CircularProgress color="inherit" size={18} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            noOptionsText="No subordinates found"
          />
        </Grid>

        {/* 4. Action Buttons */}
        <Grid item xs={12} textAlign="right">
          <Stack direction="row" justifyContent="flex-end" spacing={1.5}>
            <IconButton
              onClick={handleReset}
              sx={{
                backgroundColor: "secondary.main",
                color: "white",
                "&:hover": { backgroundColor: "secondary.dark" },
              }}
              title="Reset"
            >
              <RotateLeftIcon />
            </IconButton>
            <IconButton
              onClick={handleSubmit}
              disabled={loading}
              sx={{
                backgroundColor: "primary.main",
                color: "white",
                "&:hover": { backgroundColor: "primary.dark" },
              }}
              title="Assign"
            >
              {loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <SaveIcon />
              )}
            </IconButton>
          </Stack>
        </Grid>

      </Grid>
    </Box>
  );
}

export default memo(EmployeeHierarchyCard);
